import styled from "@emotion/native"
import { ButtonV2, Text } from "../../../ui-components"
import { Keyboard, Platform, TextInput } from "react-native"
import { useEffect, useState } from "react"

type AutoPaySecondStepProps = {
  withdrawalLimit: number
  onChangeWithdrawalLimit: (limit: number) => void
  onCancel: () => void
  isPendingAutoPay: boolean
  companyName: string
}

const MAX_WITHDRAWAL = 500000

export function AutoPayThirdStep({
  withdrawalLimit,
  onChangeWithdrawalLimit,
  onCancel,
  isPendingAutoPay,
  companyName,
}: AutoPaySecondStepProps) {
  const [localWithdrawalLimit, setLocalWithdrawalLimit] = useState(String(withdrawalLimit / 100))
  const confirmBtnDisabled = !Number(localWithdrawalLimit.replace("$", ""))
  const [keyboardHeight, setKeyboardHeight] = useState(0)
  const [isInputFocused, setInputFocused] = useState(false)

  useEffect(() => {
    const showSubscription = Keyboard.addListener(
      Platform.OS === "ios" ? "keyboardWillShow" : "keyboardDidShow",
      (e) => {
        setKeyboardHeight(e.endCoordinates.height)
      }
    )

    const hideSubscription = Keyboard.addListener(
      Platform.OS === "ios" ? "keyboardWillHide" : "keyboardDidHide",
      () => {
        setKeyboardHeight(0)
      }
    )

    return () => {
      showSubscription.remove()
      hideSubscription.remove()
    }
  }, [])

  let textInputValue = `$${localWithdrawalLimit}`
  if (textInputValue[textInputValue.length - 2] === "." && !isInputFocused) {
    textInputValue = `${textInputValue}0`
  }

  return (
    <Container onPress={Keyboard.dismiss} additionalPaddingBottom={keyboardHeight}>
      <AmountInputContainer>
        <TextInput
          value={textInputValue}
          onChangeText={(text) => {
            const newAmount = text.replace("$", "").replace(",", ".")
            const moneyRegex = /^(0|[1-9]\d*)(\.\d{0,2})?$/
            if (newAmount === "") {
              setLocalWithdrawalLimit("")
              return
            }
            if (Number(newAmount) >= MAX_WITHDRAWAL) {
              setLocalWithdrawalLimit(String(MAX_WITHDRAWAL))
              return
            }

            if (moneyRegex.test(newAmount)) {
              setLocalWithdrawalLimit(newAmount)
            }
          }}
          keyboardType="decimal-pad"
          style={{
            fontSize: 64,
            lineHeight: 64,
            fontFamily: "IBMPlexSans-Bold",
            textAlign: "center",
            height: 100,
          }}
          placeholder="0"
          maxLength={7}
          onFocus={() => {
            setInputFocused(true)
            if (localWithdrawalLimit[localWithdrawalLimit.length - 2] === ".") {
              setLocalWithdrawalLimit(`${localWithdrawalLimit}0`)
            }
          }}
          onBlur={() => {
            setInputFocused(false)
          }}
        />
      </AmountInputContainer>
      <Text fontSize={14} lineHeight={22} color="grayscale700">
        Control how much{" "}
        <Text fontSize={14} lineHeight={22} fontFamily="IBMPlexSans-Bold">
          {companyName}
        </Text>{" "}
        can withdraw from your account.
      </Text>
      <ButtonsContainer>
        <ButtonContainer>
          <ButtonV2
            title="Cancel"
            variant="blue2"
            customBackgroundColor="grayscale200"
            customHeight={50}
            textColor="grayscale950"
            onPress={onCancel}
          />
        </ButtonContainer>
        <ButtonContainer>
          <ButtonV2
            title="Confirm"
            variant={confirmBtnDisabled ? "blue" : "blue2"}
            customHeight={50}
            disabled={confirmBtnDisabled}
            loading={isPendingAutoPay}
            onPress={() =>
              onChangeWithdrawalLimit(Number(localWithdrawalLimit.replace("$", "")) * 100)
            }
          />
        </ButtonContainer>
      </ButtonsContainer>
    </Container>
  )
}

const Container = styled.Pressable<{ additionalPaddingBottom: number }>`
  gap: 12px;
  padding-bottom: ${({ additionalPaddingBottom }) => `${60 + additionalPaddingBottom}px`};
`

const AmountInputContainer = styled.View`
  padding-top: 32px;
  flex-direction: row;
  align-items: center;
  justify-content: center;
`

const ButtonsContainer = styled.View`
  flex-direction: row;
  justify-content: space-between;
  width: 100%;
  gap: 16px;
  padding-top: 16px;
`

const ButtonContainer = styled.View`
  width: 48%;
`
